// Data Retention Policy for CareerOS-AI.
import { Link } from "react-router-dom";
import LegalLayout, { bullets } from "./LegalLayout";

const EFFECTIVE = "July 16, 2026";

const SECTIONS = [
  { n: 1, id: "principle", title: "Retention principle",
    body: <p>CareerOS-AI keeps personal data only as long as necessary to provide the Service, meet legal obligations, resolve disputes, and enforce our agreements. Anything we no longer need is deleted or de-identified.</p> },
  { n: 2, id: "categories", title: "Retention by data category",
    body: (<>
      <p>The following table describes our default retention periods. Where a legal obligation requires a longer period, the longer period applies.</p>
      <div className="not-prose overflow-x-auto mt-4">
        <table className="w-full text-sm border border-border">
          <thead className="bg-card/60">
            <tr>
              <th className="text-left px-4 py-2 border-b border-border font-mono uppercase text-xs tracking-widest">Data</th>
              <th className="text-left px-4 py-2 border-b border-border font-mono uppercase text-xs tracking-widest">Default retention</th>
              <th className="text-left px-4 py-2 border-b border-border font-mono uppercase text-xs tracking-widest">Reason</th>
            </tr>
          </thead>
          <tbody className="[&_td]:px-4 [&_td]:py-2 [&_td]:border-b [&_td]:border-border">
            <tr><td>Account profile</td><td>While your account is active</td><td>To provide the Service.</td></tr>
            <tr><td>Resumes, cover letters, uploaded documents</td><td>While your account is active</td><td>To display and reprocess them for you.</td></tr>
            <tr><td>Chat &amp; interview transcripts</td><td>While your account is active or until you delete them</td><td>Session continuity.</td></tr>
            <tr><td>Financial-plan data</td><td>While your account is active</td><td>To render and simulate plans.</td></tr>
            <tr><td>AI prompt / response logs (server-side)</td><td>30 days</td><td>Abuse detection, debugging, safety review.</td></tr>
            <tr><td>Access logs, IPs, user agents</td><td>90 days</td><td>Security and abuse investigations.</td></tr>
            <tr><td>Email delivery events (Resend webhooks)</td><td>12 months</td><td>Deliverability and unsubscribe accuracy.</td></tr>
            <tr><td>Billing records (invoices, receipts, tax IDs)</td><td>7 years</td><td>Canadian tax / accounting obligations.</td></tr>
            <tr><td>Support cases</td><td>3 years after closure</td><td>Reference for repeat issues.</td></tr>
            <tr><td>Aggregated analytics</td><td>Indefinite (de-identified)</td><td>Product improvement.</td></tr>
            <tr><td>Cookie-consent choices</td><td>12 months, then re-prompt</td><td>Consent freshness.</td></tr>
            <tr><td>Account deletion request records</td><td>12 months</td><td>To prove we honored the request.</td></tr>
          </tbody>
        </table>
      </div>
    </>) },
  { n: 3, id: "account-deletion", title: "When you delete your account",
    body: (<>
      <p>Deleting your account from <Link to="/app/settings">Settings</Link>:</p>
      {bullets([
        "Immediately removes your profile, resumes, chats, and financial plans from active systems.",
        "Marks your account for permanent deletion within 30 days.",
        "Retains only the minimum records required to meet legal obligations (billing invoices for 7 years; deletion-request record for 12 months; anti-fraud fingerprints).",
      ])}
    </>) },
  { n: 4, id: "backups", title: "Backups",
    body: (<>
      <p>Our disaster-recovery backups retain snapshots of production data for up to 35 days on a rolling basis. Deleted data is purged from backups on the next backup cycle after the retention window; it is never restored except in the event of a genuine outage.</p>
    </>) },
  { n: 5, id: "de-identification", title: "De-identified and aggregate data",
    body: <p>We may keep de-identified and aggregate data (data that can no longer reasonably be linked to you) indefinitely for research, product decisions, and business reporting.</p> },
  { n: 6, id: "your-rights", title: "Your rights",
    body: <p>You can access, export, or delete your data at any time from Settings or by emailing <span className="font-mono">support@careeros-ai.com</span>. See our <Link to="/privacy">Privacy Policy</Link> for details on your rights under applicable law.</p> },
  { n: 7, id: "changes", title: "Changes to this policy",
    body: <p>We may adjust retention periods as our platform evolves. Material changes are communicated on the website. The &quot;Last Updated&quot; date reflects the current version.</p> },
];

const INTRO = (
  <p>This Data Retention Policy explains how long CareerOS-AI keeps different categories of your data and when we delete it. Read together with our <Link to="/privacy">Privacy Policy</Link>.</p>
);

export default function DataRetention() {
  return (
    <LegalLayout
      eyebrow="Legal · Data Retention"
      title="Data Retention Policy"
      subtitle="for CareerOS-AI"
      effectiveDate={EFFECTIVE}
      lastUpdated={EFFECTIVE}
      intro={INTRO}
      sections={SECTIONS}
      currentPath="/data-retention"
    />
  );
}
